import { type RefObject, useCallback } from 'react';
import { atom, useAtom } from 'jotai';

import { QUERY_KEY } from '~/api/query/keys';
import { usePatientCreateMutation } from '~/api/query/patientCreate';
import { usePatientSearchVerifyMutation } from '~/api/query/patientSearchVerify';
import { usePatientUpdateSettingsMutation } from '~/api/query/patientSettingsUpdate';
import { usePatientUpdateMutation } from '~/api/query/patientUpdate';
import { queryClient } from '~/api/query/queryClient';
import {
  type PatientCreateRequest,
  type PatientSearchVerifyResponse,
} from '~/api/types';
import type { PatientFormType } from '~/features/patientCreation/types';

import { PatientCreateSteps } from './constants';
import type {
  PatientCreationFormState,
  PatientInformationFormValues,
  PatientIntroFormRef,
  PatientIntroFormValues,
  PatientSettingsFormRef,
} from './types';
import { authState } from '../auth/state';
import { useModal } from '../modals/useModal';

const formAtom = atom<PatientCreationFormState>({
  step: PatientCreateSteps.PATIENT_INTRO,
  isNextStepDisabled: false,
  values: {},
});

const foundPatientsAtom = atom<PatientFormType>();

type UsePatientCreationFormOptions = {
  introFormRef: RefObject<PatientIntroFormRef | null>;
  settingsFormRef: RefObject<PatientSettingsFormRef | null>;
  informationFormRef: RefObject<PatientIntroFormRef | null>;
  onViewPatientProfile: (patientId: string) => Promise<void>;
};

export const usePatientCreationForm = ({
  onViewPatientProfile,
  introFormRef,
  settingsFormRef,
  informationFormRef,
}: UsePatientCreationFormOptions) => {
  const { userInfo } = authState.get();

  const [{ step, isNextStepDisabled, values }, setFormState] =
    useAtom(formAtom);
  const [foundPatient, setFoundPatient] = useAtom(foundPatientsAtom);
  const { openModal, closeModal } = useModal();
  const patientSearchVerifyMutation = usePatientSearchVerifyMutation();
  const patientCreateMutation = usePatientCreateMutation();
  const patientUpdateMutation = usePatientUpdateMutation();
  const patientUpdateSettingsMutation = usePatientUpdateSettingsMutation();

  const setIsNextStepDisabled = useCallback(
    (value: boolean) => {
      setFormState((prevState) => ({
        ...prevState,
        isNextStepDisabled: value,
      }));
    },
    [setFormState],
  );

  const setStep = useCallback(
    (newStep: PatientCreationFormState['step']) => {
      setFormState((prevState) => ({
        ...prevState,
        step: newStep,
      }));
    },
    [setFormState],
  );

  const setFormValues = useCallback(
    (newValues: Partial<PatientCreationFormState['values']>) => {
      setFormState((prevState) => ({
        ...prevState,
        isNextStepDisabled: false,
        values: {
          ...prevState.values,
          ...newValues,
        },
      }));
    },
    [setFormState],
  );

  const onModalClose = useCallback(() => {
    closeModal('PatientCreate');
  }, [closeModal]);

  const resetForm = useCallback(() => {
    setFormState({
      values: {},
      step: PatientCreateSteps.PATIENT_INTRO,
    });
  }, [setFormState]);

  const setFormValuesFromFoundPatient = useCallback(
    ({ id, phone, firstName, lastName, matchType }: PatientFormType) => {
      setFormValues({
        id,
        phone,
        firstName,
        lastName,
        matchType,
      });
    },
    [setFormValues],
  );

  const onPatientIntroSubmit = useCallback(
    async ({ phone }: PatientIntroFormValues) => {
      let foundPatientData: PatientSearchVerifyResponse | null = null;

      try {
        foundPatientData = await patientSearchVerifyMutation.mutateAsync({
          phoneNumber: phone,
          idnID: userInfo?.idnId ?? '',
        });

        let matchType: PatientFormType['matchType'];

        if (foundPatientData.status === 'archived') {
          // Patient Found Archived
          setStep(PatientCreateSteps.PATIENT_ARCHIVED_ACCOUNT_FOUND);
          setFoundPatient(foundPatientData);
          matchType = 'archived';
        } else if (foundPatientData.departmentId !== userInfo?.departmentId) {
          // Patient found Another Department
          setStep(PatientCreateSteps.PATIENT_ANOTHER_DEPARTMENT_ACCOUNT_FOUND);
          setFoundPatient(foundPatientData);
          matchType = 'department';
        } else {
          // Patient Found in the same department
          setStep(PatientCreateSteps.PATIENT_ALREADY_EXISTS);
          setFoundPatient(foundPatientData);
          matchType = 'phone';
        }
        setFormValuesFromFoundPatient({
          ...foundPatientData,
          phone,
          matchType,
        });
        setIsNextStepDisabled(false);
      } catch {
        setFormValues({ phone });
        setStep(PatientCreateSteps.PATIENT_INFORMATION);
        // Reset any previous found patients
        setFormValuesFromFoundPatient({
          id: '',
          phone,
          firstName: '',
          lastName: '',
          matchType: 'phone',
        });
      }
    },
    [
      patientSearchVerifyMutation,
      setFormValues,
      setStep,
      setFoundPatient,
      setIsNextStepDisabled,
      setFormValuesFromFoundPatient,
      userInfo,
    ],
  );

  const onPatientInformationSubmit = useCallback(
    (infoFormValues: PatientInformationFormValues) => {
      setFormValues(infoFormValues);
      setStep(PatientCreateSteps.PATIENT_SETTINGS);
    },
    [setStep, setFormValues],
  );

  const handlePatientSettingsSubmit = useCallback(async () => {
    const settingsValues = settingsFormRef.current?.getValues() ?? {};

    setFormValues(settingsValues);

    if (!userInfo?.departmentId) {
      console.error(
        'User department ID is missing. Cannot create or update patient.',
      );

      return;
    }

    const params: PatientCreateRequest = {
      patient: {
        firstName: values.firstName ?? '',
        lastName: values.lastName ?? '',
        email: values.email ?? '',
        phone: values.phone ?? '',
        dateOfBirth: values.dateOfBirth ?? '',
        zipcode: values.zipCode ?? '',
        mrn: values.mrn ?? '',
        idnId: userInfo.idnId ?? '',
        departmentId: userInfo.departmentId,
        country: 'US',
        language: 'EN',
        invitedBy: 'email',
      },
    };
    let response;

    if (values.id) {
      params.patient.id = values.id;
      response = await patientUpdateMutation.mutateAsync(params);
    } else {
      // New patient creation
      response = await patientCreateMutation.mutateAsync(params);
    }
    // Update the Settings

    await patientUpdateSettingsMutation.mutateAsync({
      id: response.id,
      displayMode: settingsValues.displayLactateData ? 1 : 0,
      highLactateAlert: {
        enabled: settingsValues.highLactateAlert ?? false,
        threshold: settingsValues.highLactateAlertAbove ?? 0,
        durationInMin: settingsValues.highLactateAlertDuration ?? 0,
      },
    });

    // Invalidate patient search query to reflect the newly created/updated patient
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY.PATIENT_SEARCH],
    });

    // Form reset
    resetForm();
    onModalClose();

    openModal('PatientAdded', {
      patientId: response.id,
      patientName: `${response.firstName} ${response.lastName}`,
      inviteBy: 'email',
      patientEmail: response.email,
      matchType: values.matchType,
      testID: 'patient-added-modal',
      onViewPatientProfile: () => {
        closeModal('PatientAdded');

        return onViewPatientProfile(response.id);
      },
    });
  }, [
    settingsFormRef,
    onModalClose,
    openModal,
    setFormValues,
    values,
    patientCreateMutation,
    patientUpdateSettingsMutation,
    patientUpdateMutation,
    userInfo,
    resetForm,
    closeModal,
    onViewPatientProfile,
  ]);

  const handlePatientInformationGoBack = useCallback(() => {
    if (!foundPatient) {
      setStep(PatientCreateSteps.PATIENT_INTRO);

      return;
    }

    switch (foundPatient.matchType) {
      case 'archived':
        setStep(PatientCreateSteps.PATIENT_ARCHIVED_ACCOUNT_FOUND);
        break;
      case 'department':
        setStep(PatientCreateSteps.PATIENT_ANOTHER_DEPARTMENT_ACCOUNT_FOUND);
        break;
      case 'phone':
        setStep(PatientCreateSteps.PATIENT_ALREADY_EXISTS);
        break;
    }
  }, [foundPatient, setStep]);

  const goToPreviousStep = useCallback(() => {
    switch (step) {
      case PatientCreateSteps.PATIENT_INFORMATION:
        handlePatientInformationGoBack();
        break;
      case PatientCreateSteps.PATIENT_ALREADY_EXISTS:
      case PatientCreateSteps.PATIENT_ANOTHER_DEPARTMENT_ACCOUNT_FOUND:
      case PatientCreateSteps.PATIENT_ARCHIVED_ACCOUNT_FOUND:
        setStep(PatientCreateSteps.PATIENT_INTRO);
        break;
      case PatientCreateSteps.PATIENT_SETTINGS:
        setStep(PatientCreateSteps.PATIENT_INFORMATION);
        break;
      default:
        resetForm();
        onModalClose();
    }
  }, [handlePatientInformationGoBack, onModalClose, setStep, step, resetForm]);

  const getNextStepButtonLabel = useCallback(() => {
    switch (step) {
      case PatientCreateSteps.PATIENT_INTRO:
      case PatientCreateSteps.PATIENT_INFORMATION:
      case PatientCreateSteps.PATIENT_ANOTHER_DEPARTMENT_ACCOUNT_FOUND:
      case PatientCreateSteps.PATIENT_ARCHIVED_ACCOUNT_FOUND:
        return 'Next';
      case PatientCreateSteps.PATIENT_SETTINGS:
        return 'Complete';
      case PatientCreateSteps.PATIENT_ALREADY_EXISTS:
        return 'Go to Patient';
      default:
        return 'Close';
    }
  }, [step]);

  const goToNextStep = useCallback(async () => {
    switch (step) {
      case PatientCreateSteps.PATIENT_INTRO:
        await introFormRef.current?.submitForm();
        break;
      case PatientCreateSteps.PATIENT_ANOTHER_DEPARTMENT_ACCOUNT_FOUND:
      case PatientCreateSteps.PATIENT_ARCHIVED_ACCOUNT_FOUND:
        setStep(PatientCreateSteps.PATIENT_INFORMATION);
        break;
      case PatientCreateSteps.PATIENT_ALREADY_EXISTS:
        if (!foundPatient) {
          return;
        }

        onModalClose();

        resetForm();
        if (foundPatient.id) await onViewPatientProfile(foundPatient.id);

        break;
      case PatientCreateSteps.PATIENT_INFORMATION:
        await informationFormRef.current?.submitForm();
        break;
      case PatientCreateSteps.PATIENT_SETTINGS:
        await handlePatientSettingsSubmit();
        break;
      default:
        onModalClose();
        break;
    }
  }, [
    onViewPatientProfile,
    step,
    introFormRef,
    setStep,
    informationFormRef,
    handlePatientSettingsSubmit,
    onModalClose,
    resetForm,
    foundPatient,
  ]);

  // Flags that causing loading behavior for the form
  const isFormLoading =
    patientSearchVerifyMutation.isPending || patientCreateMutation.isPending;

  return {
    values,
    isFormLoading,
    currentStep: step,
    isNextStepDisabled,
    foundPatient,
    setIsNextStepDisabled,
    goToPreviousStep,
    goToNextStep,
    onModalClose,
    onPatientIntroSubmit,
    onPatientInformationSubmit,
    resetForm,
    getNextStepButtonLabel,
  };
};
